import React, { useState } from 'react';
import { Modal } from '../../../../UiKit';
import { Plus } from 'lucide-react';
import { Delete } from 'sushiweb/Icons';
import { useMutationCall } from '../../../../api/react-client';
import { OBHQ_ENDPOINTS, FIXED_MODE, FIXED_TIMEZONE } from './constants';
import { useClient } from '../../../../api/api-client';
import toast from 'react-hot-toast';
import Button from '../../../../components/ComponentsV1/Button';

const EMPTY_GROUP = () => ({
  _id: Date.now() + Math.random(),
  day_of_week: '',
  start_hour: '',
  start_min: '',
  end_hour: '',
  end_min: '',
});

const FixedBadge = ({ label }) => (
  <span className='tw-inline-flex tw-items-center tw-bg-green-100 tw-text-green-700 tw-px-4 tw-py-2 tw-rounded-[0.6rem] tw-text-[14px] tw-font-medium tw-border tw-border-solid tw-border-green-300'>
    {label}
  </span>
);

const OBHQModal = ({ isOpen, onClose, item, formOptions, refetchData, onSaveStart }) => {
  const client = useClient();
  const isUpdate = !!item;

  const [queueName, setQueueName] = useState(isUpdate ? item?.queue_name ?? '' : '');
  const [cannedResponse, setCannedResponse] = useState(
    isUpdate ? item?.canned_response ?? '' : '',
  );
  const [groups, setGroups] = useState(() => {
    if (isUpdate && Array.isArray(item?.business_hours) && item.business_hours.length > 0) {
      return [...item.business_hours]
        .sort((a, b) => (a?.day_of_week ?? 0) - (b?.day_of_week ?? 0))
        .map((bh) => ({
        _id: Math.random(),
        day_of_week: bh?.day_of_week != null ? String(bh.day_of_week) : '',
        start_hour: bh?.start_hour != null ? String(bh.start_hour) : '',
        start_min: bh?.start_min != null ? String(bh.start_min) : '',
        end_hour: bh?.end_hour != null ? String(bh.end_hour) : '',
        end_min: bh?.end_min != null ? String(bh.end_min) : '',
      }));
    }
    return [EMPTY_GROUP()];
  });

  const { mutate: saveConfig, isLoading: isSaving } = useMutationCall(
    {
      onSuccess: (res) => {
        toast.success(res?.message ?? (isUpdate ? 'Config updated successfully.' : 'Config created successfully.'));
        refetchData();
      },
      onError: () => {
        toast.error('Something went wrong. Please try again.');
        refetchData();
      },
    },
    OBHQ_ENDPOINTS.CREATE_UPDATE,
    ['obhq_create_update'],
    { doNotShowSuccessToast: true },
    (data) => client(OBHQ_ENDPOINTS.CREATE_UPDATE, { data }),
  );

  const addGroup = () => setGroups((prev) => {
    if (!Array.isArray(prev)) return [EMPTY_GROUP()];
    return [...prev, EMPTY_GROUP()];
  });

  const removeGroup = (id) =>
    setGroups((prev) => {
      if (!Array.isArray(prev)) return [EMPTY_GROUP()];
      return prev.filter((g) => g?._id !== id);
    });

  const updateGroup = (id, field, value) =>
    setGroups((prev) => {
      if (!Array.isArray(prev)) return [EMPTY_GROUP()];
      return prev.map((g) => (g?._id === id ? { ...g, [field]: value } : g));
    });

  const handleSubmit = () => {
    if (!queueName || !queueName.trim()) {
      toast.error('Please select a queue name.');
      return;
    }
    if (typeof cannedResponse !== 'string' || !cannedResponse.trim()) {
      toast.error('Please enter a canned response.');
      return;
    }
    if (!Array.isArray(groups)) {
      toast.error('Invalid business hours data.');
      return;
    }
    const validGroups = groups.filter(
      (g) =>
        g &&
        g.day_of_week !== '' &&
        g.day_of_week != null &&
        g.start_hour !== '' &&
        g.start_hour != null &&
        g.start_min !== '' &&
        g.start_min != null &&
        g.end_hour !== '' &&
        g.end_hour != null &&
        g.end_min !== '' &&
        g.end_min != null,
    );
    if (validGroups.length === 0) {
      toast.error('Please add at least one complete business hours entry.');
      return;
    }
    const invalidTime = validGroups.find((g) => {
      const start = Number(g?.start_hour ?? 0) * 60 + Number(g?.start_min ?? 0);
      const end = Number(g?.end_hour ?? 0) * 60 + Number(g?.end_min ?? 0);
      return end <= start;
    });
    if (invalidTime) {
      const dayName = formOptions?.days_of_week?.find(
        (d) => String(d?.id ?? '') === String(invalidTime?.day_of_week ?? ''),
      )?.name ?? `Day ${invalidTime?.day_of_week ?? 'unknown'}`;
      toast.error(`End time must be after start time for ${dayName}.`);
      return;
    }
    const payload = {
      queue_name: queueName,
      timezone: FIXED_TIMEZONE,
      mode: FIXED_MODE,
      canned_response: cannedResponse.trim(),
      business_hours: validGroups.map(({ _id, ...g }) => ({
        day_of_week: Number(g.day_of_week),
        start_hour: Number(g.start_hour),
        start_min: Number(g.start_min),
        end_hour: Number(g.end_hour),
        end_min: Number(g.end_min),
      })),
    };
    onSaveStart?.();
    saveConfig(payload);
  };

  const queueOptions = formOptions?.queues?.map((q) => ({ id: q, name: q })) ?? [];
  const dayOptions = formOptions?.days_of_week ?? [];
  const hourOptions = formOptions?.hours ?? [];
  const minuteOptions = formOptions?.minutes ?? [];

  const selectClass =
    'tw-w-full tw-border tw-border-solid tw-border-grey-300 tw-rounded-[0.6rem] tw-px-3 tw-py-3 tw-bg-white tw-text-grey-800 tw-cursor-pointer tw-text-[14px]';
  const labelClass = 'tw-font-medium tw-text-[#434343] tw-text-[16px] tw-mb-3';

  return (
    <Modal
      isVisible={isOpen}
      padding={0}
      paddingTop={'1rem'}
      type='large'
      height='100%'
      maxHeight='85vh'
      containerStyles={{ margin: '16px 0 20px 0', padding: '0 20px' }}
      titleComponent={
        <span className='tw-text-[1.8rem] tw-font-medium tw-text-zinc-600'>
          {isUpdate ? (
            'Update Queue'
          ) : (
            'Create New Queue'
          )}
        </span>
      }
      handleClose={onClose}
    >
      <div className='tw-flex tw-flex-col tw-gap-8'>
        {/* Queue Name */}
        <div>
          <div className={labelClass}>Queue Name</div>
          {isUpdate ? (
            <FixedBadge label={item?.queue_name ?? ''} />
          ) : (
            <select
              className={selectClass}
              value={queueName}
              onChange={(e) => setQueueName(e.target.value)}
            >
              <option value=''>Select a queue</option>
              {Array.isArray(queueOptions) && queueOptions.map((q) => (
                <option key={q?.id} value={q?.id}>
                  {q?.name}
                </option>
              ))}
            </select>
          )}
        </div>

        {/* Fixed: Timezone */}
        <div>
          <div className={labelClass}>Timezone</div>
          <FixedBadge label='Asia/Kolkata (IST)' />
        </div>

        {/* Fixed: Mode */}
        <div>
          <div className={labelClass}>Mode</div>
          <FixedBadge label='Canned Response' />
        </div>

        {/* Canned Response */}
        <div>
          <div className={labelClass}>Canned Response</div>
          <textarea
            className='tw-w-full tw-border tw-border-solid tw-border-grey-300 tw-rounded-[0.6rem] tw-px-3 tw-py-3 tw-bg-white tw-text-grey-800 tw-resize-vertical tw-text-[14px] tw-min-h-[80px]'
            placeholder='Enter the canned response message...'
            value={cannedResponse}
            onChange={(e) => setCannedResponse(e.target.value)}
          />
        </div>

        {/* Business Hours Groups */}
        <div>
          <div className={labelClass}>Business Hours</div>
          <div className='tw-flex tw-flex-col tw-gap-4'>
            {Array.isArray(groups) && groups.map((group) => (
              <div key={group?._id} className='tw-flex tw-gap-4 tw-items-center'>
                <div className='tw-flex tw-flex-col tw-gap-3 tw-w-full tw-border tw-border-solid tw-border-[#e0e0e0] tw-rounded-[0.6rem] tw-p-5 tw-bg-[#f9f9f9]'>
                  <div className='tw-grid tw-grid-cols-5 tw-gap-3'>
                    {/* Day */}
                    <div>
                      <div className='tw-text-[13px] tw-font-medium tw-text-grey-600 tw-mb-2'>
                        Day
                      </div>
                      <select
                        className={selectClass}
                        value={group?.day_of_week ?? ''}
                        onChange={(e) =>
                          updateGroup(group?._id, 'day_of_week', e.target.value)
                        }
                      >
                        <option value=''>Day</option>
                        {Array.isArray(dayOptions) && dayOptions.map((d) => (
                          <option key={d?.id} value={d?.id}>
                            {d?.name}
                          </option>
                        ))}
                      </select>
                    </div>
                    {/* Start Hour */}
                    <div>
                      <div className='tw-text-[13px] tw-font-medium tw-text-grey-600 tw-mb-2'>
                        Start Hour
                      </div>
                      <select
                        className={selectClass}
                        value={group?.start_hour ?? ''}
                        onChange={(e) =>
                          updateGroup(group?._id, 'start_hour', e.target.value)
                        }
                      >
                        <option value=''>Hour</option>
                        {Array.isArray(hourOptions) && hourOptions.map((h) => (
                          <option key={h?.id} value={h?.id}>
                            {h?.label}
                          </option>
                        ))}
                      </select>
                    </div>
                    {/* Start Min */}
                    <div>
                      <div className='tw-text-[13px] tw-font-medium tw-text-grey-600 tw-mb-2'>
                        Start Min
                      </div>
                      <select
                        className={selectClass}
                        value={group?.start_min ?? ''}
                        onChange={(e) =>
                          updateGroup(group?._id, 'start_min', e.target.value)
                        }
                      >
                        <option value=''>Min</option>
                        {Array.isArray(minuteOptions) && minuteOptions.map((m) => (
                          <option key={m?.id} value={m?.id}>
                            {m?.label}
                          </option>
                        ))}
                      </select>
                    </div>
                    {/* End Hour */}
                    <div>
                      <div className='tw-text-[13px] tw-font-medium tw-text-grey-600 tw-mb-2'>
                        End Hour
                      </div>
                      <select
                        className={selectClass}
                        value={group?.end_hour ?? ''}
                        onChange={(e) =>
                          updateGroup(group?._id, 'end_hour', e.target.value)
                        }
                      >
                        <option value=''>Hour</option>
                        {Array.isArray(hourOptions) && hourOptions.map((h) => (
                          <option key={h?.id} value={h?.id}>
                            {h?.label}
                          </option>
                        ))}
                      </select>
                    </div>
                    {/* End Min */}
                    <div>
                      <div className='tw-text-[13px] tw-font-medium tw-text-grey-600 tw-mb-2'>
                        End Min
                      </div>
                      <select
                        className={selectClass}
                        value={group?.end_min ?? ''}
                        onChange={(e) =>
                          updateGroup(group?._id, 'end_min', e.target.value)
                        }
                      >
                        <option value=''>Min</option>
                        {Array.isArray(minuteOptions) && minuteOptions.map((m) => (
                          <option key={m?.id} value={m?.id}>
                            {m?.label}
                          </option>
                        ))}
                      </select>
                    </div>
                  </div>
                </div>
                {/* Remove group */}
                <div
                  className={`tw-aspect-square tw-h-16 ${
                    Array.isArray(groups) && groups.length === 1 ? 'tw-invisible' : ''
                  }`}
                >
                  <button
                    type='button'
                    aria-label='Remove business hours rule'
                    className='tw-cursor-pointer tw-aspect-square tw-h-full tw-flex tw-items-center tw-justify-center tw-rounded-[0.6rem] tw-bg-red-100 hover:tw-bg-[#ffe3e6] tw-transition-all tw-duration-200 tw-ease-in-out tw-border tw-border-solid tw-border-[#ff5f5f]'
                    onClick={() => removeGroup(group?._id)}
                  >
                    <Delete size={16} color='#ff5f5f' />
                  </button>
                </div>
              </div>
            ))}
          </div>

          {/* Add Rule */}
          <div className='tw-h-16 tw-w-full tw-flex tw-items-center tw-mt-4'>
            <button
              type='button'
              className='tw-w-full tw-cursor-pointer tw-rounded-[0.6rem] tw-h-full tw-flex tw-items-center tw-justify-center tw-bg-[#eeffe9] tw-font-medium tw-text-[#318616] tw-border tw-border-solid tw-border-[#318616] hover:tw-bg-[#e9ffe2] tw-transition-all tw-duration-200 tw-ease-in-out'
              onClick={addGroup}
            >
              <Plus size={18} color='#318616' />
              <span className='tw-text-[16px] tw-ml-2'>Add Rule</span>
            </button>
          </div>
        </div>

        {/* Submit */}
        <div className='tw-mt-4 tw-w-full tw-flex tw-justify-end tw-pb-4'>
          <Button
            onClick={handleSubmit}
            loading={isSaving}
            disabled={isSaving}
            title={
              isUpdate ? 'Save Changes' : 'Save'
            }
          />
        </div>
      </div>
    </Modal>
  );
};

export default OBHQModal;
